import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { CsvFile } from "./CsvFile"
import { FileUpload } from "./FileUpload"
import { BatchFileValidator } from "./validation/BatchFileValidator"
import { useBatchPageContext } from "./useBatchPageContext"
import { setInitialErrors, setItems } from "./reducer"
import { BatchItem } from "./BatchItem"
import { CircularProgress, Grid, Typography } from "@material-ui/core"
import styles from "./Upload.module.scss"
import BatchPageStyles from "./BatchPage.module.scss"
import { Step, useStepControlsContext } from "../../features/StepControls/useStepControlsContext"
import { StepControls } from "../../features/StepControls/StepControls"
import { BatchItemValidator } from "./validation/BatchItemValidator"
import { useApolloClient } from "@apollo/client"
import { formHasValidationErrors, getErrorMsgs } from "./validation/ValidationHelpers"

export const UploadFileStepDescription: FunctionComponent = props => (
    <>
        <Grid item data-testid="upload-file-description">
            <Typography variant="caption">
                Upload a .csv file - Must be <b>UTF-8 encoded</b>
                <br /> Limit 100 rows.
                <br /> Allowed column names are:
            </Typography>
            <Typography variant="caption">
                <ul className={styles.Upload__list}>
                    <li>
                        <b>name:</b> <i>the name of the prompt</i>
                    </li>
                    <li>
                        <b>source:</b> <i>the source text of the prompt</i>
                    </li>
                    <li>
                        <b>target:</b> <i>the target text of the prompt (this is optional)</i>
                    </li>
                </ul>
            </Typography>
        </Grid>
        <Grid item>
            <StepControls />
        </Grid>
    </>
)

export class NoItemsError extends Error {
    constructor() {
        super("File must contain at least 1 row")
    }
}

export class TooManyItemsError extends Error {
    constructor() {
        super("File must contain less than 100 rows")
    }
}

export const Upload: FunctionComponent = props => {
    const { dispatch, state } = useBatchPageContext()
    const { activate, deactivate, show, hide } = useStepControlsContext()
    const [fileValidationErrors, setFileValidationErrors] = useState<Error[]>([])
    const [loading, setLoading] = useState(false)
    const fileValidation = new BatchFileValidator()
    const client = useApolloClient()

    async function handleFile(file: CsvFile) {
        setFileValidationErrors([])
        dispatch(setItems([]))
        deactivate(Step.NEXT)

        setLoading(true)
        const fileData = await fileValidation.validate(file)
        const json = await file.toJson()
        if (fileData.errors.length > 0) {
            setFileValidationErrors(fileData.errors)
        } else if (json.length === 0) {
            setFileValidationErrors([new NoItemsError()])
        } else if (json.length > 100) {
            setFileValidationErrors([new TooManyItemsError()])
        } else {
            await upload(file)
        }
        setLoading(false)
    }

    async function upload(file: CsvFile) {
        const items: BatchItem[] = await file.toJson()
        items.forEach((item, index) => {
            item.index = index
        })
        const initialErrors = await new BatchItemValidator(client).validate(items)
        dispatch(setInitialErrors(initialErrors))
        items.forEach(item => {
            item.include = true
            item.valid = !formHasValidationErrors(initialErrors[item.index])
            item.errorStringForCSV = getErrorMsgs(initialErrors[item.index])
        })
        dispatch(setItems(items))
    }

    const canProceed = useMemo(() => {
        return (
            fileValidationErrors.length === 0 &&
            !state.containsValidationErrors &&
            !state.containsOperationErrors &&
            state.batchItems.filter(o => o.include).length > 0 &&
            !loading
        )
    }, [
        fileValidationErrors,
        state.containsValidationErrors,
        state.containsOperationErrors,
        state.batchItems,
        loading
    ])

    useEffect(() => {
        if (canProceed) {
            activate(Step.NEXT)
        } else {
            deactivate(Step.NEXT)
        }
    }, [canProceed])

    useEffect(() => {
        show(Step.NEXT)
        hide(Step.BACK)
    }, [])

    return (
        <Grid
            container
            alignItems="stretch"
            className={BatchPageStyles.Upload__griditem}
            direction="column"
            justify="flex-start"
        >
            <Grid item>
                <FileUpload onUpload={handleFile} fileValidationErrors={fileValidationErrors} />
            </Grid>
            <Grid item>
                <div className={styles.Upload}>{loading && <CircularProgress />}</div>
            </Grid>
        </Grid>
    )
}
